import { isDevMode } from "@angular/core";
import { Item } from "../../pojo/Item";
import type { Proto } from "../../pojo/Proto";
import { S25ItemI } from "../../pojo/S25ItemI";
import { S25Util } from "../s25-util";

export type InternalState = {
    _wanted: Record<number, FetchConfig>;
    _inFlight: Record<number, FetchConfig>;
    _failures: Record<number, number>;
    _wantedQueries: Partial<Record<QueryKey, QueryRequest>>;
    _inFlightQueries: Partial<Record<QueryKey, QueryRequest>>;
    _loadedQueries: Partial<Record<QueryKey, FetchConfig>>;
    _failedQueries: Partial<Record<QueryKey, number>>;
};
export namespace InternalState {
    type Updater = (state: InternalState) => Partial<InternalState>;
    export function clearWanted(): Updater {
        return (state) => {
            const patch: Partial<InternalState> = {};
            if (Object.keys(state._wanted).length) {
                patch._wanted = {};
            }
            if (Object.keys(state._wantedQueries).length) {
                patch._wantedQueries = {};
            }
            return patch;
        };
    }

    export function addIdsToWanted(ids: number[], fetch: FetchConfig): Updater {
        if (!ids.length) return noOpUpdater();
        return (state) => {
            const _wanted = { ...state._wanted };
            ids.filter((id) => !FetchConfig.isSuperset(state._inFlight[id], fetch)).forEach(
                (id) => (_wanted[id] = FetchConfig.merge(_wanted[id], fetch)),
            );
            return { _wanted };
        };
    }

    export function addQueryToWanted(queryKey: QueryKey, request: QueryRequest): Updater {
        return (state) => {
            if (FetchConfig.isSuperset(state._inFlightQueries[queryKey]?.fetch, request.fetch)) return {};
            return {
                _wantedQueries: {
                    ...state._wantedQueries,
                    [queryKey]: {
                        ...request,
                        fetch: FetchConfig.merge(state._wantedQueries[queryKey]?.fetch, request.fetch),
                    },
                },
            };
        };
    }

    export function markQueryInFlight(queryKey: QueryKey, queryRequest: QueryRequest): Updater {
        return (state) => ({
            _inFlightQueries: {
                ...state._inFlightQueries,
                [queryKey]: queryRequest,
            },
        });
    }

    export function markQueryComplete(queryKey: QueryKey): Updater {
        return (state) => {
            const queries = { ...state._inFlightQueries };
            delete queries[queryKey];
            return {
                _inFlightQueries: queries,
            };
        };
    }

    export function markQueryLoaded(queryKey: QueryKey, handles: FetchConfig): Updater {
        return (state) => ({
            _loadedQueries: {
                ...state._loadedQueries,
                [queryKey]: FetchConfig.merge(state._loadedQueries[queryKey], handles),
            },
        });
    }

    export function clearQueryCache(queryKey: QueryKey): Updater {
        return (state) => {
            if (!state._loadedQueries[queryKey] && !state._failedQueries[queryKey]) {
                return {};
            }
            const loadedQueries = { ...state._loadedQueries };
            delete loadedQueries[queryKey];
            const failedQueries = { ...state._failedQueries };
            delete failedQueries[queryKey];
            return { _loadedQueries: loadedQueries, _failedQueries: failedQueries };
        };
    }

    export function trackFailedQuery(queryKey: QueryKey, error?: unknown): Updater {
        return (state) => {
            if (error || state._failedQueries[queryKey]) {
                const failures = { ...state._failedQueries };
                if (error) {
                    failures[queryKey] = (failures[queryKey] ?? 0) + 1;
                } else {
                    delete failures[queryKey];
                }
                return {
                    _failedQueries: failures,
                };
            } else {
                return {};
            }
        };
    }

    export function markIdsInFlight(ids: number[], fetchConfig: FetchConfig): Updater {
        if (!ids.length) return noOpUpdater();
        return (state) => {
            const _inFlight = { ...state._inFlight };
            ids.forEach((id) => (_inFlight[id] = FetchConfig.merge(_inFlight[id], fetchConfig)));
            return { _inFlight };
        };
    }

    export function markIdsComplete(ids: number[], fetch: FetchConfig) {
        if (!ids.length) {
            return noOpUpdater();
        }
        return (state: InternalState) => {
            const _inFlight = { ...state._inFlight };
            ids.filter((id) => FetchConfig.subtract(state._inFlight[id], fetch)).forEach((id) => delete _inFlight[id]);
            return { _inFlight };
        };
    }

    export function trackFailedIds(ids: number[], error?: unknown) {
        if (!ids.length) {
            return noOpUpdater();
        }
        if (error) {
            return (state: InternalState) => {
                const _failures = { ...state._failures };
                ids.forEach((id) => (_failures[id] = (_failures[id] ?? 0) + 1));
                return { _failures };
            };
        } else {
            return clearPreviousFailures(ids);
        }
    }

    function clearPreviousFailures(ids: number[]) {
        return (state: InternalState) => {
            const previousFailures = ids.filter((id) => !!state._failures[id]);
            if (previousFailures) {
                const _failures = { ...state._failures };
                previousFailures.forEach((id) => delete _failures[id]);
                return { _failures };
            } else {
                return {};
            }
        };
    }

    function noOpUpdater() {
        return () => ({});
    }
}

export type FetchConfig = { [prop in FetchConfig.Prop | "all"]?: boolean };
export namespace FetchConfig {
    export const Prop = ["name", "description", "favorite"] as const;
    export type Prop = (typeof Prop)[number];

    export const Default: FetchConfig = { name: true };

    /**
     * Construct a FetchConfig based on whether the predicate returns a truthy value
     * for each property.
     */
    function fromProps(predicate: (prop: Prop) => any): FetchConfig {
        const propEntries = Prop.filter(predicate).map((p) => [p, true]);
        if (propEntries.length === Prop.length) return { all: true };
        return Object.fromEntries(propEntries) as FetchConfig;
    }

    /** whether A is a superset of B, i.e. A contains all properties that B contains */
    export function isSuperset(a: FetchConfig = {}, b: FetchConfig = {}): boolean {
        return a.all || Prop.every((prop) => !(b[prop] || b.all) || a[prop]);
    }

    /** return a FetchConfig containing only properties present in both A and B */
    export function intersection(a: FetchConfig = {}, b: FetchConfig = {}): FetchConfig {
        return fromProps((prop) => (a[prop] || a.all) && (b[prop] || b.all));
    }

    /** return a FetchConfig containing all properties present in either A or B, in other words, a union */
    export function merge(a: FetchConfig = {}, b: FetchConfig = {}): FetchConfig {
        if (a.all || b.all) return { all: true };
        return fromProps((prop) => a[prop] || b[prop]);
    }

    /** return a FetchConfig containing properties in A that are not in B */
    export function subtract(a: FetchConfig = {}, b: FetchConfig = {}): FetchConfig {
        if (b.all) return {};
        return fromProps((prop) => (a[prop] || a.all) && !b[prop]);
    }

    export function isEmpty(config: FetchConfig): boolean {
        return !config.all && !Prop.some((prop) => config[prop]);
    }

    const FetchPropToField: Record<FetchConfig.Prop, keyof S25ItemI> = {
        name: "itemName",
        description: "itemDesc",
        favorite: "favorite",
    };

    /**
     * In dev mode, scan items that are being stored to ensure the fetchConfig covers
     * all loaded properties. This ensures others will be able to reuse these items
     * without fetching redundant information.
     */
    export function assertFetchConfigMatches(items: S25ItemI<any>[], loaded: FetchConfig) {
        if (!isDevMode()) return;
        for (const [prop, field] of Object.entries(FetchPropToField) as [FetchConfig.Prop, keyof S25ItemI][]) {
            if (loaded[prop] || loaded.all) continue;
            const unexpected = items.filter((item) => item[field] != null);
            if (unexpected.length) {
                console.warn(
                    `ItemStore: ${unexpected.length} item(s) have "${field}" but you're not declaring "${prop}" in the fetch config. In order for others to use the property, add it to your fetch config (or if you're loading all properties, set {all: true})`,
                    { loaded, sampleItem: unexpected[0] },
                );
            }
        }
    }
}

export type StoredItem<T extends string | number | undefined> = Item<T> & {
    _loaded: FetchConfig;
};

export namespace StoredItem {
    export function hasProps(
        entity: StoredItem<any> | undefined,
        props: FetchConfig,
    ): entity is NonNullable<StoredItem<any>> {
        if (!entity || !("_loaded" in entity)) return false;
        return FetchConfig.isSuperset(entity._loaded, props);
    }

    export function toItem<T extends string | number | undefined>(
        entity: StoredItem<T> | undefined,
        id: number,
        typeId: T,
    ): StoredItem<T> {
        return entity ? { ...entity } : { itemId: id, itemTypeId: typeId, _loaded: {} };
    }
}

export type QueryKey = "everything" | `{${string}}`;
export function QueryKey(params?: Record<string, unknown>): QueryKey {
    if (!params || !Object.keys(params).length) return "everything";
    return JSON.stringify(params, Object.keys(params).sort()) as `{${string}}`;
}

export type QueryRequest = {
    rawParams?: Record<string, unknown>;
    fetch: FetchConfig;
};

export type ItemStoreID = number | Proto.NumericalString;
export type IdItem<T extends string | number | undefined> = S25ItemI<T> & { itemId: ItemStoreID };
export namespace ItemStoreId {
    export function fromItem(c: ItemStoreID | S25ItemI<unknown>): number {
        return typeof c === "object" ? S25Util.toInt(c.itemId) : S25Util.toInt(c);
    }
}

export type ItemCriteria<T extends number | string | undefined> =
    | (S25ItemI<T> & { itemId: ItemStoreID })[]
    | ItemStoreID[]
    | ItemCriteria.Query;

export namespace ItemCriteria {
    export type Query = { everything: true } | { params: Record<string, unknown> };

    export function isQuery(criteria: ItemCriteria<any>): criteria is Query {
        return !Array.isArray(criteria);
    }
}
